import { zodResolver } from "@hookform/resolvers/zod";
import { fidelityProposalFormDataSchema } from "@repo/core/helpers/zod.schemas";
import { ROUTES } from "@repo/core/routes";
import { useProposalFlowNavigation } from "../../utils/proposalFlowNavigation";
import { useFidelityProposal } from "../hooks/useFidelityProposal";
import { FidelityProposalFormType } from "@repo/core/stores/proposalForm/types";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { CommonResource } from "@repo/core/services/resources/common";
import { apiInstance } from "@repo/core/services/instances/api";
import { useRouteParams } from "@repo/core/hooks/useRouteParams";
import { mapToFidelityProposalForm } from "../hooks/useFidelityProposal/mappers";

const commonResource = new CommonResource(apiInstance);
const REVIEW_NAVIGATION_DELAY_MS = 150;

const waitForReviewNavigation = (): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, REVIEW_NAVIGATION_DELAY_MS);
  });

export const FidelityPersonalDataController = () => {
  const isSubmittingRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { back, goToNextStep, goToPreviousStep } = useProposalFlowNavigation();
  const { proposalId } = useRouteParams();

  const {
    proposalData,
    getProposalData,
    updateProposalData,
    resetFidelityProposalStore
  } = useFidelityProposal();

  // Check if we already have matching data in the store (same pattern as Credit)
  const hasMatchingData =
    proposalData &&
    proposalId &&
    proposalData.lngProposta &&
    String(proposalData.lngProposta) === String(proposalId);

  // Only fetch if we have a proposalId but no matching data in store
  const shouldFetchProposal = !!proposalId && !hasMatchingData;

  const {
    data: queryProposalData,
    isLoading: isLoadingProposal,
    isFetching: isFetchingProposal
  } = useQuery({
    queryKey: ["fidelityProposal", proposalId],
    queryFn: () => getProposalData(proposalId!),
    enabled: shouldFetchProposal,
    refetchOnMount: !hasMatchingData ? "always" : false,
    refetchOnWindowFocus: false,
    staleTime: 0
  });

  // Sync query data to store when query returns data but store doesn't have matching data (same as Credit)
  useEffect(() => {
    if (
      queryProposalData &&
      proposalId &&
      String(queryProposalData.lngProposta) === String(proposalId) &&
      (!proposalData ||
        !proposalData.lngProposta ||
        String(proposalData.lngProposta) !== String(proposalId))
    ) {
      updateProposalData(queryProposalData);
    }
  }, [queryProposalData, proposalId, proposalData, updateProposalData]);

  // Only show loading if we're fetching and don't have matching data
  const isLoading =
    !!proposalId &&
    shouldFetchProposal &&
    (isLoadingProposal || isFetchingProposal);

  // Get personal data from proposalData (same reactive pattern as Credit with useMemo + values)
  const personalData = useMemo(() => {
    if (!proposalData) {
      return {
        birthday: "",
        gender: "",
        martialStatus: "",
        profession: ""
      };
    }
    const formData = mapToFidelityProposalForm(proposalData);

    console.log("formData", proposalData);

    return {
      birthday: formData.birthday || "",
      gender: formData.gender || "",
      martialStatus: formData.martialStatus || "",
      profession: formData.profession || ""
    };
  }, [proposalData]);

  /** Stable `values` for RHF so unrelated store updates (e.g. strProfissaoDescricao) do not reset the form. */
  const formValues = useMemo(
    () => ({
      birthday: personalData.birthday,
      gender: personalData.gender,
      martialStatus: personalData.martialStatus,
      profession: personalData.profession
    }),
    [
      personalData.birthday,
      personalData.gender,
      personalData.martialStatus,
      personalData.profession
    ]
  );

  const { data: martialStatusOptions } = useQuery({
    queryKey: ["martialStatus"],
    queryFn: () => commonResource.fetchMartialStatus(),
    select: (data) => {
      return data.data.map((item) => ({
        value: item.strCodEstadoCivil,
        label: item.strDescricao
      }));
    }
  });

  const { data: professionOptions, isLoading: isProfessionsLoading } = useQuery(
    {
      queryKey: ["professions"],
      queryFn: () => commonResource.fetchProfessions(),
      select: (data) => {
        return data.data.map((item) => ({
          value: item.strCodProfissao,
          label: item.strDescricao
        }));
      }
    }
  );

  const hasUpdatedProfessionDescription = useRef(false);

  const { control, handleSubmit } = useForm<FidelityProposalFormType>({
    resolver: zodResolver(fidelityProposalFormDataSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    values: formValues
  });

  // Update profession description in store when profession code exists but description is not saved
  useEffect(() => {
    if (
      !proposalData ||
      !professionOptions ||
      hasUpdatedProfessionDescription.current
    ) {
      return;
    }

    const needsProfessionDescription =
      proposalData.strProfissao && !proposalData.strProfissaoDescricao;

    if (needsProfessionDescription) {
      const professionLabel =
        professionOptions.find(
          (option) => option.value === proposalData.strProfissao
        )?.label || "";

      if (professionLabel) {
        hasUpdatedProfessionDescription.current = true;
        updateProposalData({ strProfissaoDescricao: professionLabel });
      }
    }
  }, [proposalData, professionOptions, updateProposalData]);

  const handleOnSubmitForm = handleSubmit(async (formData) => {
    if (!proposalData || isSubmittingRef.current) {
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      // Find the profession label from the options
      const selectedProfessionLabel =
        professionOptions?.find(
          (option) => option.value === formData.profession
        )?.label || "";

      const updateData: Partial<typeof proposalData> = {
        strEstadoCivil: formData.martialStatus,
        strProfissao: formData.profession,
        strSexo: formData.gender as string,
        // Store profession description for display purposes (not sent to backend)
        strProfissaoDescricao: selectedProfessionLabel
      };

      // Update date if provided and valid, otherwise keep existing
      if (formData.birthday && formData.birthday.trim() !== "") {
        try {
          const dateStr = formData.birthday.split("/").reverse().join("-");
          const date = new Date(dateStr);
          if (!isNaN(date.getTime())) {
            updateData.datDataNascimento = date.toISOString();
          } else {
            updateData.datDataNascimento = proposalData.datDataNascimento;
          }
        } catch (error) {
          console.error("Error parsing date:", error);
          updateData.datDataNascimento = proposalData.datDataNascimento;
        }
      }

      updateProposalData(updateData);
      await waitForReviewNavigation();
      goToNextStep(ROUTES.fidelityProposalReview);
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  });

  const handleGoBack = () => {
    goToPreviousStep(ROUTES.proposals);
  };

  const handleClose = () => {
    resetFidelityProposalStore();
    back();
  };

  return {
    control,
    handleOnSubmitForm,
    handleGoBack,
    handleClose,
    martialStatusOptions,
    professionOptions,
    isProfessionsLoading,
    isLoadingProposal: isLoading,
    isSubmitting
  };
};
